import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2, Mic, Square, PhoneCall, PhoneOff, Send } from "lucide-react";
import { air, airBlob, airTranscribe } from "./api";

export function TestConsole({ token, centres }) {
  const [center, setCenter] = useState(centres.find((c) => c.code === "PB-HSR")?.code || centres[0]?.code || "");
  const [call, setCall] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [voiceOn, setVoiceOn] = useState(true);
  const [rec, setRec] = useState(null);
  const [state, setState] = useState({});
  const [lead, setLead] = useState(null);
  const audio = useRef(null);
  const box = useRef(null);
  useEffect(() => { if (box.current) box.current.scrollTop = box.current.scrollHeight; }, [msgs]);

  const play = async (t) => {
    if (!voiceOn) return;
    try { const b = await airBlob("voice/speak", token, { text: t, center }); audio.current.src = URL.createObjectURL(b); await audio.current.play(); } catch (e) { toast.error(e.message); }
  };
  const start = async () => {
    setBusy(true); setLead(null);
    try { const r = await air("test/start", token, { center, caller: "" }); setCall(r.call_id); setMsgs([{ role: "assistant", text: r.reply }]); setState({}); play(r.reply); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const send = async (t) => {
    if (!t.trim()) return;
    setBusy(true); setText("");
    setMsgs((m) => [...m, { role: "user", text: t }]);
    try { const r = await air("test/turn", token, { call_id: call, text: t }); setMsgs((m) => [...m, { role: "assistant", text: r.reply }]); setState(r); play(r.reply); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const end = async () => {
    setBusy(true);
    try { const r = await air("test/end", token, { call_id: call, text: "" }); setLead(r.lead); setCall(null); toast.success(r.lead ? `Lead ${r.lead.ref} created` : "Call ended — no actionable enquiry"); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const mic = async () => {
    if (rec) { rec.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const r = new MediaRecorder(stream); const chunks = [];
      r.ondataavailable = (e) => chunks.push(e.data);
      r.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop()); setRec(null); setBusy(true);
        try { const t = await airTranscribe(token, new Blob(chunks, { type: "audio/webm" })); setBusy(false); await send(t); } catch (e) { toast.error(e.message); setBusy(false); }
      };
      r.start(); setRec(r);
    } catch { toast.error("Microphone permission denied"); }
  };

  return (
    <div className="grid md:grid-cols-3 gap-3" data-testid="air-console">
      <div className="md:col-span-2 rounded border p-3 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <select className="h-9 rounded border text-sm" value={center} disabled={!!call} onChange={(e) => setCenter(e.target.value)} data-testid="air-console-centre">{centres.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}</select>
          {!call ? <Button onClick={start} disabled={busy || !center} data-testid="air-console-start"><PhoneCall className="mr-1 h-4 w-4" />Start test call</Button>
            : <Button variant="destructive" onClick={end} disabled={busy} data-testid="air-console-end"><PhoneOff className="mr-1 h-4 w-4" />End call</Button>}
          <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={voiceOn} onChange={(e) => setVoiceOn(e.target.checked)} data-testid="air-console-voice" />Speak replies aloud</label>
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
        </div>
        <div ref={box} className="h-80 overflow-y-auto space-y-2 rounded bg-muted/30 p-2" data-testid="air-console-transcript">
          {msgs.map((m, i) => <div key={i} className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${m.role === "user" ? "ml-auto bg-primary text-primary-foreground" : "bg-background border"}`} data-testid={`air-msg-${i}`}>{m.text}</div>)}
          {!msgs.length && <p className="text-xs text-muted-foreground">Start a test call, then speak (mic) or type as a guest would — in English, Hindi or Marathi. Test calls are marked as tests and do not reach customers.</p>}
        </div>
        <div className="flex gap-2">
          <Button variant={rec ? "destructive" : "outline"} disabled={!call || (busy && !rec)} onClick={mic} data-testid="air-console-mic">{rec ? <><Square className="mr-1 h-4 w-4" />Stop</> : <><Mic className="mr-1 h-4 w-4" />Speak</>}</Button>
          <Input disabled={!call || busy} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send(text)} placeholder="Type what the guest says…" data-testid="air-console-input" />
          <Button disabled={!call || busy} onClick={() => send(text)} data-testid="air-console-send"><Send className="h-4 w-4" /></Button>
        </div>
        <audio ref={audio} hidden />
      </div>
      <div className="rounded border p-3 text-xs space-y-2" data-testid="air-console-capture">
        <p className="font-semibold">Captured so far</p>
        <p>Intent: <Badge variant="outline">{state.intent || "—"}</Badge> {state.escalate && <Badge className="bg-red-100 text-red-800" data-testid="air-console-escalate">Escalate to team</Badge>}</p>
        <p>Language: {state.language || "—"}</p>
        {Object.entries(state.captured || {}).map(([k, v]) => <p key={k}><span className="text-muted-foreground">{k.replace(/_/g, " ")}:</span> {String(v)}</p>)}
        {lead && <div className="rounded bg-emerald-50 p-2" data-testid="air-console-lead">Lead <b>{lead.ref}</b> ({lead.enquiry_type}) saved to Call Leads · {lead.follow_up_status.replace(/_/g, " ")}</div>}
      </div>
    </div>
  );
}
